import type {
  AccessPageDto,
  AuditEventDto,
  CollectStatusDto,
  CoverageDto,
  DecompteDto,
  DevicesDto,
  JobsPage,
  LastImportDto,
  RetentionDto,
  SessionDto,
  SummaryDto,
} from '@mnemo/shared';

/**
 * Client d'API — une seule porte, un seul format d'erreur.
 *
 * ⚠️ Une panne réseau n'est PAS une session expirée (Charte §5). Les deux produisent un
 * appel raté, mais l'une demande de réessayer et l'autre de se reconnecter. Les confondre,
 * c'est renvoyer les gens vers une page de connexion quand c'est le serveur qui est
 * tombé — et leur faire croire qu'ils ont fait quelque chose de mal.
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}

/** Délai mural : un pair qui accepte la connexion sans jamais répondre gèlerait la page. */
const TIMEOUT_MS = 15_000;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      signal: controller.signal,
      // 🪤 On n'annonce « application/json » que si l'on envoie vraiment du JSON.
      // Un POST sans corps mais avec cet en-tête fait échouer l'analyse côté Fastify :
      // la route n'est jamais exécutée, et on voit une erreur qui n'a aucun rapport avec
      // ce qu'on demandait. C'est ce qui a fait croire des jours que la déconnexion
      // « ne marchait pas ».
      headers: {
        ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError('NETWORK', 0);
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    let code = 'INTERNAL';
    try {
      const body = (await response.json()) as { error?: { code?: string } };
      code = body.error?.code ?? code;
    } catch {
      /* corps illisible : le code de statut suffira */
    }
    throw new ApiError(code, response.status);
  }
  return (await response.json()) as T;
}

export type SessionResponse = SessionDto & {
  personas: { id: string; name: string; isAdmin: boolean }[];
};

export const api = {
  session: () => request<SessionResponse>('/api/session'),
  switchPersona: (persona: string) =>
    request<{ persona: string }>('/api/session/persona', {
      method: 'POST',
      body: JSON.stringify({ persona }),
    }),
  coverage: () => request<CoverageDto>('/api/coverage'),
  lastImport: () => request<LastImportDto>('/api/last-import'),
  establishments: () => request<{ items: { code: string; jobs: number }[] }>('/api/establishments'),
  costCenters: (establishment?: string | null) =>
    request<{ items: { name: string; jobs: number }[] }>(
      `/api/cost-centers${establishment ? `?establishment=${encodeURIComponent(establishment)}` : ''}`,
    ),
  jobs: (params: URLSearchParams) => request<JobsPage>(`/api/jobs?${params.toString()}`),
  summary: (params: URLSearchParams) => request<SummaryDto>(`/api/summary?${params.toString()}`),
  health: () => request<{ status: string; version: string }>('/api/health'),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  exportUrl: (params: URLSearchParams) => `/api/jobs/export?${params.toString()}`,
  byPerson: (params: URLSearchParams) =>
    request<DecompteDto>(`/api/jobs/by-person?${params.toString()}`),
  byPersonExportUrl: (params: URLSearchParams) => `/api/jobs/by-person/export?${params.toString()}`,
  admin: {
    access: () => request<AccessPageDto>('/api/admin/access'),
    events: (kind?: 'login' | 'export') =>
      request<{ items: AuditEventDto[] }>(`/api/admin/events${kind ? `?kind=${kind}` : ''}`),
    collect: () => request<CollectStatusDto>('/api/admin/collect'),
    retention: () => request<RetentionDto>('/api/admin/retention'),
    devices: () => request<DevicesDto>('/api/admin/devices'),
  },
};
